// @ts-strict-ignore
import {
  PlaceAutocomplete,
  PlaceType,
  PlaceDetails,
  SearchPlacesRequest,
  SearchPlaceResult,
} from '@dialogue/coredata'
import { AxiosResponse } from 'axios'
import Config from 'APP/Config'
import { AutocompleteOption } from 'APP/Converse/Inputs/AutocompleteInput'
import { isCanada } from 'APP/Helpers/RegionHelpers'
import { logger } from 'APP/Lib/Datadog'
import { createPlacesApi } from 'APP/Store/utils/emergencyRoom'
import i18n from 'APP/I18n'

const MIN_SEARCH_LENGTH = 2

type PlaceResult = PlaceAutocomplete | SearchPlaceResult

export const getPlacesSupportedType = (placeType: string): PlaceType => {
  switch (placeType) {
    case PlaceType.pharmacy:
    case 'establishment': // BM place_type for pharmacy
      return PlaceType.pharmacy
    case PlaceType.address: // BM place_type for address
    default:
      return PlaceType.address
  }
}

export async function getPlacesAutocomplete(
  { searchTerm, placeType }: { searchTerm: string; placeType: PlaceType | undefined },
  { signal }: { signal: AbortSignal }
): Promise<PlaceAutocomplete[]> {
  const client = createPlacesApi()
  const response: AxiosResponse<PlaceAutocomplete[]> = await client.getPlacesAutocomplete(
    {
      searchTerm,
      placeType: placeType,
      regionCode: isCanada() ? [Config.REGION as string] : undefined,
    },
    { signal }
  )
  return response.data ?? []
}

export async function getPlaceDetails(placeId: string): Promise<PlaceDetails | undefined> {
  const client = createPlacesApi()
  const response = (await client.getPlaceDetails({ placeId })) as AxiosResponse
  const data = response.data
  return data?.data ?? data
}

export async function getPlacesSearch(
  { searchPlacesRequest }: { searchPlacesRequest: SearchPlacesRequest },
  { signal }: { signal: AbortSignal }
): Promise<SearchPlaceResult[]> {
  const client = createPlacesApi()
  const response = await client.searchPlaces(
    {
      searchPlacesRequest,
    },
    { signal }
  )
  return response?.data?.data || []
}

/**
 * Maps a place result to an AutocompleteOption
 * Handles different address field names between APIs
 */
const mapPlaceToOption = (place: PlaceResult): AutocompleteOption => {
  const address =
    ('full_address' in place ? place?.full_address : null) ??
    ('address' in place ? place?.address : null) ??
    ''

  return {
    value: place?.place_id ?? '',
    label: place?.name ?? '',
    caption: address,
  }
}

export interface LoadPlaceOptionsParams {
  input: string
  placeType: string
  signal: AbortSignal
  origin?: { latitude: number; longitude: number } | null
}

/**
 * Loads pharmacy place options using searchPlaces API
 */
const loadPharmacyOptions = async ({
  input,
  signal,
  origin,
}: {
  input: string
  signal: AbortSignal
  origin?: { latitude: number; longitude: number } | null
}): Promise<AutocompleteOption[]> => {
  const pharmacySuffix = i18n.t('SelfNavigationScreen.specialties.pharmacy.q') ?? 'pharmacy'
  const query = `${input} ${pharmacySuffix.toLowerCase()}`

  try {
    const places = await getPlacesSearch(
      {
        searchPlacesRequest: {
          query,
          ...(origin && {
            origin: { latitude: origin.latitude, longitude: origin.longitude },
          }),
        },
      },
      { signal }
    )

    if (!places || !Array.isArray(places)) {
      return []
    }

    return places.map(mapPlaceToOption)
  } catch (error) {
    logger.error('Failed to load pharmacy options:', {}, error)
    return []
  }
}

/**
 * Loads address place options using getPlacesAutocomplete API
 */
const loadAddressOptions = async ({
  input,
  placeType,
  signal,
}: {
  input: string
  placeType: PlaceType
  signal: AbortSignal
}): Promise<AutocompleteOption[]> => {
  try {
    const places = await getPlacesAutocomplete(
      {
        searchTerm: input,
        placeType,
      },
      { signal }
    )

    if (!places || !Array.isArray(places)) {
      return []
    }

    return places.map(mapPlaceToOption)
  } catch (error) {
    logger.error('Failed to load place options:', {}, error)
    return []
  }
}

/**
 * Loads place options based on place type
 * Uses different APIs for pharmacy vs address searches
 */
export async function loadPlaceOptions({
  input,
  placeType,
  signal,
  origin,
}: LoadPlaceOptionsParams): Promise<AutocompleteOption[]> {
  if (input.length < MIN_SEARCH_LENGTH) {
    return []
  }

  const supportedType = getPlacesSupportedType(placeType)

  if (supportedType === PlaceType.pharmacy) {
    return loadPharmacyOptions({ input, signal, origin })
  }

  return loadAddressOptions({ input, placeType: supportedType, signal })
}
